import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { useAuthStore } from '@presentation/stores/authStore';
import { QUERY_KEYS } from '@shared/constants';
import type {
  CreateWorkspacePayload,
  UpdateWorkspacePayload,
} from '@core/repositories/WorkspaceRepository';

export const useWorkspaces = () => {
  return useQuery({
    queryKey: QUERY_KEYS.workspaces,
    queryFn: () => container.workspaces.list(),
  });
};

export const useWorkspace = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.workspace(id ?? ''),
    queryFn: () => container.workspaces.get(id as string),
    enabled: Boolean(id),
  });
};

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateWorkspacePayload) => container.workspaces.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces }),
  });
};

export const useUpdateWorkspace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateWorkspacePayload) => container.workspaces.update(id, payload),
    onSuccess: (workspace) => {
      queryClient.setQueryData(QUERY_KEYS.workspace(id), workspace);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces });
    },
  });
};

export const useArchiveWorkspace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => container.workspaces.archive(id),
    onSuccess: (workspace) => {
      queryClient.setQueryData(QUERY_KEYS.workspace(id), workspace);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces });
    },
  });
};

export const useRestoreWorkspace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => container.workspaces.restore(id),
    onSuccess: (workspace) => {
      queryClient.setQueryData(QUERY_KEYS.workspace(id), workspace);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces });
    },
  });
};

export const useTransferWorkspace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => container.workspaces.transfer(id, userId),
    onSuccess: (workspace) => {
      queryClient.setQueryData(QUERY_KEYS.workspace(id), workspace);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces });
    },
  });
};

export const useSwitchWorkspace = () => {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (id: string) => container.workspaces.switchTo(id),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(QUERY_KEYS.currentUser, user);
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.workspaces.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.workspaces }),
  });
};
